/**
 * Booking Intake: Draft Review screen.
 *
 *  - Shows extracted fields as editable inputs.
 *  - Shows the original raw text + warnings + source channel.
 *  - Lets staff pick the actual vehicle from the fleet (autocomplete).
 *  - "Save draft" persists edits; "Approve and send confirmation" creates a
 *    booking (status awaiting_customer_confirmation) + a confirmation_tokens
 *    row, reusing the existing /api/public/confirm/{token} engine.
 *  - After approval the SendConfirmationPanel slides in.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, AlertTriangle, ShieldCheck, Loader2, Send,
  Trash2, Save, Copy, CheckCircle2, Mail, Pencil,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const SOURCE_OPTIONS = [
  ["messenger", "Messenger"],
  ["whatsapp", "WhatsApp"],
  ["viber", "Viber"],
  ["sms", "SMS"],
  ["facebook", "Facebook"],
  ["phone", "Phone call"],
  ["walk_in", "Walk-in"],
  ["quick_note", "Quick note"],
  ["other", "Other"],
];

const SOURCE_TIPS = {
  messenger: "Copy this message and paste it back into the Messenger chat.",
  whatsapp: "Copy this message and paste it into WhatsApp.",
  viber: "Copy this message and paste it into Viber.",
  sms: "Copy this message and send it by text.",
  facebook: "Copy this message and reply on Facebook.",
  phone: "Read the booking back, then send the link via SMS or chat.",
  walk_in: "Show the customer the link on screen, or send via SMS/Messenger.",
};

const FIELD_STATUS_META = {
  confident: { label: "Confident", cls: "bg-emerald-100 text-emerald-800" },
  uncertain: { label: "Uncertain: verify", cls: "bg-amber-100 text-amber-800" },
  missing: { label: "Missing", cls: "bg-rose-100 text-rose-800" },
  edited: { label: "Edited by you", cls: "bg-slate-200 text-slate-800" },
};

function StatusPill({ status }) {
  const meta = FIELD_STATUS_META[status];
  if (!meta) return null;
  return (
    <span className={`ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function fieldStatus(key, currentVal, draft) {
  const norm = (v) => (v === undefined || v === null ? "" : String(v).trim());
  const current = norm(currentVal);
  const original = norm(draft?.extracted?.[key]);
  if (current !== original) return current ? "edited" : (draft?.field_sources?.[key] ? "edited" : "missing");
  if (!current) return "missing";
  const src = draft?.field_sources?.[key];
  if (src === "rule") return "confident";
  if (src === "llm") return "uncertain";
  return "uncertain";
}

export default function BookingIntakeDraft() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [returningMatches, setReturningMatches] = useState([]);
  const [overrideIssue, setOverrideIssue] = useState(null);
  const [overrideNote, setOverrideNote] = useState("");

  // Editable working copy of the extracted fields.
  const [fields, setFields] = useState({});
  // Opt-in auto-send channels (side task). Manual copy ALWAYS available.
  const [autoSend, setAutoSend] = useState({ sms: false, whatsapp: false, messenger: false });
  const [channelStatus, setChannelStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/booking-intake/drafts/${id}`);
      setDraft(data);
      setFields({ ...(data.extracted || {}) });
      const lookup = data.extracted?.customer_email || data.extracted?.customer_phone;
      if (lookup && !["staff_approved", "confirmation_sent", "customer_confirmed"].includes(data.status)) {
        // Reuses the same lookup BookingNew.jsx's customer autocomplete uses.
        // A match here means this phone/email already has prior bookings
        // with this business: surfaced so staff can reuse the customer's
        // name-on-file instead of whatever the parser guessed.
        api.get("/customers/search", { params: { q: lookup, limit: 3 } })
          .then((r) => setReturningMatches(r.data || []))
          .catch(() => setReturningMatches([]));
      } else {
        setReturningMatches([]);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Light fleet load for the vehicle picker.
    api.get("/vehicles").then((r) => setFleet(r.data || [])).catch(() => {});
    // Discover which channels are available right now so we only show
    // checkboxes for the ones the owner can actually use.
    api.get("/intake/channels/overview").then((r) => setChannelStatus(r.data))
      .catch(() => setChannelStatus(null));
  }, []);

  const isApproved = useMemo(() => !!draft && [
    "staff_approved", "confirmation_sent",
    "customer_confirmed", "customer_requested_change",
  ].includes(draft.status), [draft]);

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const saveDraft = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/booking-intake/drafts/${id}`, {
        extracted: fields,
      });
      setDraft(data);
      setFields({ ...(data.extracted || {}) });
      toast.success("Draft saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!fields.matched_vehicle_id) {
      toast.error("Pick a vehicle before approving.");
      return;
    }
    if (!fields.customer_email) {
      toast.error("Add a customer email so we can send the confirmation link.");
      return;
    }
    if (!fields.start_datetime || !fields.end_datetime) {
      toast.error("Pickup and return date/time are required.");
      return;
    }
    setApproving(true);
    try {
      const auto_send_channels = Object.entries(autoSend)
        .filter(([, v]) => v).map(([k]) => k);
      const { data } = await api.post(
        `/booking-intake/drafts/${id}/approve`,
        {
          customer_name: fields.customer_name || "",
          customer_email: fields.customer_email,
          customer_phone: fields.customer_phone || "",
          item_id: fields.matched_vehicle_id,
          start_datetime: fields.start_datetime,
          end_datetime: fields.end_datetime,
          pickup_location: fields.pickup_location || "",
          return_location: fields.return_location || "",
          price: Number(fields.price || 0),
          deposit_required: Number(fields.deposit || 0),
          customer_visible_notes: fields.customer_notes || "",
          internal_notes: fields.internal_notes || "",
          send_confirmation_now: true,
          auto_send_channels,
          source_channel: draft.source_channel || "quick_note",
          override_note: overrideIssue ? overrideNote : undefined,
        },
      );
      toast.success("Draft approved: confirmation link generated.");
      (data.auto_send_results || []).forEach((r) => {
        if (r.ok) toast.success(`${r.channel} → ${r.status || "sent"}`);
        else toast.error(`${r.channel} send failed: ${r.error}`);
      });
      setDraft(data.draft);
      // keep working copy aligned
      setFields({ ...(data.draft?.extracted || {}) });
      setOverrideIssue(null);
      setOverrideNote("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "warning_override_required") {
        setOverrideIssue(detail);
        toast.error("This vehicle/date has a conflict or warning: add an override note to continue.");
      } else if (e?.response?.status === 409 && detail?.code === "vehicle_reservation_busy") {
        toast.error("This vehicle is being booked by another request right now: try again in a moment.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setApproving(false);
    }
  };

  const dismissDuplicate = async () => {
    try {
      const { data } = await api.post(`/booking-intake/drafts/${id}/dismiss-duplicate`);
      setDraft(data);
      toast.success("Kept as a separate inquiry.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const discard = async () => {
    if (!confirm("Discard this draft? This cannot be undone.")) return;
    try {
      await api.post(`/booking-intake/drafts/${id}/discard`);
      toast.success("Draft discarded.");
      navigate("/app/booking-intake");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return (
    <div className="text-slate-500 flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading draft…
    </div>
  );
  if (!draft) return <div className="text-slate-500">Draft not found.</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="booking-draft-review-page">
      <Link to="/app/booking-intake"
             className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-emerald-700">
        <ArrowLeft className="w-4 h-4" /> Back to Booking Intake
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Review booking draft</h1>
          <p className="text-slate-600 text-sm">
            Source: <strong>{draft.source_channel?.replace("_", " ")}</strong> · Status:{" "}
            <strong data-testid="draft-status">{draft.status}</strong>
          </p>
        </div>
        {!isApproved && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={discard}
              data-testid="draft-discard-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-rose-700 hover:bg-rose-50 text-sm"
            >
              <Trash2 className="w-4 h-4" /> Discard
            </button>
            <button
              type="button"
              onClick={saveDraft}
              disabled={saving}
              data-testid="draft-save-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save draft
            </button>
            <button
              type="button"
              onClick={approve}
              disabled={approving}
              data-testid="draft-approve-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg btn-brand text-sm font-medium disabled:opacity-60"
            >
              {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Approve & send confirmation
            </button>
          </div>
        )}
      </header>

      {!isApproved && channelStatus && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm"
              data-testid="auto-send-panel">
          <div className="font-semibold text-slate-800 mb-1">
            Auto-send confirmation after approval
          </div>
          <p className="text-xs text-slate-500 mb-2">
            Opt-in. Email always sends if a customer email is present. Manual
            Copy buttons remain available no matter what.
          </p>
          <div className="flex flex-wrap gap-3">
            {[
              ["sms", "SMS",
                !!channelStatus.platform?.sms?.provider_configured],
              ["whatsapp", "WhatsApp",
                !!channelStatus.platform?.whatsapp?.platform_configured],
              ["messenger", "Messenger",
                !!channelStatus.platform?.messenger?.app_configured],
            ].map(([k, label, available]) => (
              <label key={k}
                      className={`inline-flex items-center gap-1.5 text-sm ${
                          available ? "" : "opacity-50 cursor-not-allowed"
                        }`}>
                <input type="checkbox"
                        data-testid={`auto-send-${k}`}
                        disabled={!available}
                        checked={!!autoSend[k]}
                        onChange={(e) =>
                          setAutoSend((s) => ({ ...s, [k]: e.target.checked }))} />
                {label}
                {!available && <span className="text-xs text-slate-400">(not configured)</span>}
              </label>
            ))}
          </div>
        </div>
      )}

      {(draft.warnings || []).length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4"
              data-testid="draft-warnings">
          <div className="flex items-center gap-2 font-medium text-amber-900 mb-1">
            <AlertTriangle className="w-4 h-4" /> Things to double-check
          </div>
          <ul className="list-disc pl-5 text-sm text-amber-900/90 space-y-0.5">
            {draft.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {!isApproved && draft.possible_duplicate && !draft.possible_duplicate_dismissed && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3"
              data-testid="possible-duplicate-banner">
          <div className="flex items-center gap-2 font-medium text-amber-900">
            <AlertTriangle className="w-4 h-4" /> Possible Duplicate Inquiry
          </div>
          <p className="text-xs text-amber-900/80">
            Same contact and same requested start date/time as{" "}
            {draft.possible_duplicate.kind === "draft" ? "another draft" : "an existing booking"} below.
            Nothing has been merged automatically — decide what to do with it.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Existing</div>
              <div className="font-medium mt-0.5">{draft.possible_duplicate.customer_name || "—"}</div>
              <div className="text-slate-600">{draft.possible_duplicate.vehicle_text || draft.possible_duplicate.vehicle_id || "—"}</div>
              <div className="text-slate-600">{draft.possible_duplicate.start_datetime || "—"} → {draft.possible_duplicate.end_datetime || "—"}</div>
              <div className="text-slate-500 text-xs mt-1">Status: {draft.possible_duplicate.status || "—"}</div>
            </div>
            <div className="rounded-lg border-2 border-amber-400 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest text-amber-700">Current</div>
              <div className="font-medium mt-0.5">{fields.customer_name || "—"}</div>
              <div className="text-slate-600">{fields.vehicle_text || "—"}</div>
              <div className="text-slate-600">{fields.start_datetime || "—"} → {fields.end_datetime || "—"}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to={draft.possible_duplicate.kind === "draft"
                ? `/app/booking-intake/drafts/${draft.possible_duplicate.id}`
                : `/app/bookings/${draft.possible_duplicate.id}`}
              data-testid="duplicate-open-existing"
              className="text-xs font-medium px-2.5 py-1.5 rounded-md border border-amber-300 bg-white hover:bg-amber-100 text-amber-900"
            >
              Open Existing
            </Link>
            <button type="button" onClick={dismissDuplicate} data-testid="duplicate-keep-separate"
                    className="text-xs font-medium px-2.5 py-1.5 rounded-md border border-amber-300 bg-white hover:bg-amber-100 text-amber-900">
              Keep as Separate Inquiry
            </button>
          </div>
        </div>
      )}

      {!isApproved && returningMatches.length > 0 && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-4" data-testid="returning-customer-banner">
          <div className="font-medium text-sky-900 mb-1">Possible returning customer</div>
          <div className="space-y-1.5">
            {returningMatches.map((m) => (
              <div key={m.email} className="flex flex-wrap items-center justify-between gap-2 text-sm text-sky-900/90">
                <span>
                  <strong>{m.name || m.email}</strong> · {m.email}{m.phone ? ` · ${m.phone}` : ""}
                  {": "}{m.booking_count} previous booking{m.booking_count === 1 ? "" : "s"} with you
                </span>
                <button
                  type="button"
                  data-testid={`use-returning-customer-${m.email}`}
                  onClick={() => {
                    setFields((f) => ({
                      ...f,
                      customer_name: m.name || f.customer_name,
                      customer_email: m.email || f.customer_email,
                      customer_phone: m.phone || f.customer_phone,
                    }));
                  }}
                  className="text-xs font-medium px-2 py-1 rounded-md border border-sky-300 bg-white hover:bg-sky-100 text-sky-800"
                >
                  Use their details on file
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {overrideIssue && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 space-y-2" data-testid="draft-override-panel">
          <div className="font-medium text-rose-900">{overrideIssue.message || "This booking has a conflict or warning."}</div>
          <Field
            label="Override note (required to continue)" testId="f-override-note"
            value={overrideNote}
            onChange={setOverrideNote}
            multiline
          />
          <button
            type="button"
            onClick={approve}
            disabled={approving || !overrideNote.trim()}
            data-testid="draft-approve-with-override-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-rose-700 text-white text-sm font-medium disabled:opacity-50"
          >
            {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Approve with override
          </button>
        </div>
      )}

      <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
        <h2 className="font-display text-lg font-semibold">Extracted details</h2>
        <p className="text-xs text-slate-500 -mt-2">
          Each field shows whether it was confidently extracted, extracted but uncertain,
          missing, or edited by you: verify anything not marked Confident before approving.
        </p>
        <Grid>
          <Field label="Customer name" testId="f-name"
                  value={fields.customer_name || ""}
                  onChange={(v) => setField("customer_name", v)}
                  disabled={isApproved}
                  status={fieldStatus("customer_name", fields.customer_name, draft)} />
          <Field label="Customer phone" testId="f-phone"
                  value={fields.customer_phone || ""}
                  onChange={(v) => setField("customer_phone", v)}
                  disabled={isApproved}
                  status={fieldStatus("customer_phone", fields.customer_phone, draft)} />
          <Field label="Customer email" testId="f-email" type="email"
                  value={fields.customer_email || ""}
                  onChange={(v) => setField("customer_email", v)}
                  disabled={isApproved}
                  status={fieldStatus("customer_email", fields.customer_email, draft)} />
          <VehiclePicker
            value={fields.matched_vehicle_id || ""}
            onChange={(id) => setField("matched_vehicle_id", id)}
            fleet={fleet}
            disabled={isApproved}
            status={fieldStatus("matched_vehicle_id", fields.matched_vehicle_id, draft)}
            candidates={draft.vehicle_candidates || []}
          />
          <Field label="Pickup date/time (ISO)" testId="f-start"
                  value={fields.start_datetime || ""}
                  onChange={(v) => setField("start_datetime", v)}
                  disabled={isApproved}
                  placeholder="2026-07-14T08:00:00+08:00"
                  status={fieldStatus("start_datetime", fields.start_datetime, draft)} />
          <Field label="Return date/time (ISO)" testId="f-end"
                  value={fields.end_datetime || ""}
                  onChange={(v) => setField("end_datetime", v)}
                  disabled={isApproved}
                  placeholder="2026-07-16T17:00:00+08:00"
                  status={fieldStatus("end_datetime", fields.end_datetime, draft)} />
          <Field label="Pickup location" testId="f-pickup"
                  value={fields.pickup_location || ""}
                  onChange={(v) => setField("pickup_location", v)}
                  disabled={isApproved}
                  status={fieldStatus("pickup_location", fields.pickup_location, draft)} />
          <Field label="Return location" testId="f-return"
                  value={fields.return_location || ""}
                  onChange={(v) => setField("return_location", v)}
                  disabled={isApproved}
                  status={fieldStatus("return_location", fields.return_location, draft)} />
          <Field label="Price (PHP/day or total)" testId="f-price" type="number"
                  value={fields.price ?? ""}
                  onChange={(v) => setField("price", v)}
                  disabled={isApproved}
                  status={fieldStatus("price", fields.price, draft)} />
          <Field label="Deposit" testId="f-deposit" type="number"
                  value={fields.deposit ?? ""}
                  onChange={(v) => setField("deposit", v)}
                  disabled={isApproved}
                  status={fieldStatus("deposit", fields.deposit, draft)} />
        </Grid>

        <Field
          label="Customer-visible notes" testId="f-customer-notes"
          value={fields.customer_notes || ""}
          onChange={(v) => setField("customer_notes", v)}
          disabled={isApproved}
          multiline
        />
        <Field
          label="Internal notes (not shown to customer)" testId="f-internal-notes"
          value={fields.internal_notes || ""}
          onChange={(v) => setField("internal_notes", v)}
          disabled={isApproved}
          multiline
        />
      </section>

      {isApproved && draft.confirmation_link && (
        <SendConfirmationPanel draft={draft} onLogged={(d) => setDraft(d)} />
      )}

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="font-display text-lg font-semibold mb-2">Original text</h2>
        <pre data-testid="draft-original-text"
              className="whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3">
{draft.original_text}
        </pre>
      </section>
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, value, onChange, disabled, testId, type, placeholder, multiline, status }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label}
        {status && <StatusPill status={status} />}
      </span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          data-testid={testId}
          placeholder={placeholder}
          className="mt-1 w-full min-h-[80px] rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500"
        />
      ) : (
        <input
          type={type || "text"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          data-testid={testId}
          placeholder={placeholder}
          className="mt-1 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm disabled:bg-slate-50 disabled:text-slate-500"
        />
      )}
    </label>
  );
}

function VehiclePicker({ value, onChange, fleet, disabled, status, candidates = [] }) {
  const otherCandidates = candidates.filter((c) => c.id !== value);
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        Vehicle (from your fleet)
        {status && <StatusPill status={status} />}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid="f-vehicle"
        className="mt-1 w-full h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm disabled:bg-slate-50"
      >
        <option value="">- pick a vehicle -</option>
        {fleet.map((v) => (
          <option key={v.id} value={v.id}>
            {v.name}{v.category ? ` · ${v.category}` : ""}
          </option>
        ))}
      </select>
      {!disabled && otherCandidates.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="vehicle-candidates">
          <span className="text-[11px] text-slate-500 self-center">Did you mean:</span>
          {otherCandidates.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`vehicle-candidate-${c.id}`}
              onClick={() => onChange(c.id)}
              className="text-xs px-2 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
            >
              {c.name}{c.category ? ` · ${c.category}` : ""} ({c.score}%)
            </button>
          ))}
        </div>
      )}
    </label>
  );
}

function SendConfirmationPanel({ draft, onLogged }) {
  const [busy, setBusy] = useState(false);
  const [copiedKind, setCopiedKind] = useState(null);
  const [channel, setChannel] = useState(draft.source_channel || "messenger");
  const link = draft.confirmation_link;
  const message = useMemo(() => {
    // We don't have the prebuilt message after refresh, so rebuild client-side.
    const name = (draft.extracted?.customer_name || "there").split(" ")[0] || "there";
    return `Hi ${name}, please review and confirm your booking here:\n\n${link}\n\n`
         + "Please check the vehicle, pickup date/time, return date/time, location, "
         + "price, deposit, and notes. If something is wrong, you can request a "
         + "change before pickup.";
  }, [draft, link]);

  const isWaiting = draft.status === "confirmation_sent"
                  || draft.status === "customer_requested_change";
  const isConfirmed = draft.status === "customer_confirmed";

  const logAction = async (action, opts = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/booking-intake/drafts/${draft.id}/delivery-log`,
        { action, channel, ...opts },
      );
      onLogged?.(data.draft || draft);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (kind, text) => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      // Fallback for sandboxed iframes / older browsers: write to a
      // temporary textarea, select, execCommand('copy').
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopiedKind(kind);
      setTimeout(() => setCopiedKind(null), 1500);
      await logAction(kind === "message" ? "copy_message" : "copy_link");
      toast.success("Copied to clipboard.");
    } else {
      toast.error("Browser blocked copy: select the message text and copy it manually.");
    }
  };

  return (
    <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 space-y-4"
              data-testid="send-confirmation-panel">
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-1" />
        <div className="flex-1">
          <h2 className="font-display text-lg font-semibold text-emerald-900">
            {isConfirmed
              ? "Customer confirmed this booking"
              : "Send the customer confirmation link"}
          </h2>
          {!isConfirmed && (
            <p className="text-sm text-emerald-900/80 mt-0.5">
              Manual &ldquo;copied&rdquo; only means staff copied the message, not that
              the customer received it. Re-send anytime until the customer
              confirms.
            </p>
          )}
        </div>
      </div>

      {!isConfirmed && (
        <>
          <label className="block max-w-xs">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-900/80">
              Send via
            </span>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              data-testid="delivery-channel"
              className="mt-1 w-full h-10 rounded-lg border border-emerald-300 bg-white px-3 text-sm"
            >
              {SOURCE_OPTIONS.map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
            {SOURCE_TIPS[channel] && (
              <p className="text-xs text-emerald-900/80 mt-1">{SOURCE_TIPS[channel]}</p>
            )}
          </label>

          <div className="rounded-lg bg-white border border-emerald-200 p-3 font-mono text-xs whitespace-pre-wrap break-words"
                data-testid="confirmation-message">
            {message}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => copyText("message", message)}
              disabled={busy}
              data-testid="copy-confirmation-message"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg btn-brand text-sm font-semibold disabled:opacity-60"
            >
              {copiedKind === "message"
                ? <CheckCircle2 className="w-4 h-4" />
                : <Copy className="w-4 h-4" />}
              Copy confirmation message
            </button>
            <button
              type="button"
              onClick={() => copyText("link", link)}
              disabled={busy}
              data-testid="copy-confirmation-link"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-300 bg-white text-sm font-medium disabled:opacity-60"
            >
              {copiedKind === "link"
                ? <CheckCircle2 className="w-4 h-4" />
                : <Copy className="w-4 h-4" />}
              Copy link only
            </button>
            <button
              type="button"
              onClick={() => logAction("resend_email")}
              disabled={busy}
              data-testid="resend-email-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-300 bg-white text-sm font-medium disabled:opacity-60"
            >
              <Mail className="w-4 h-4" /> Resend email
            </button>
            <button
              type="button"
              onClick={() => logAction("mark_contacted_manually",
                                       { note: "Contacted via " + channel })}
              disabled={busy}
              data-testid="mark-contacted-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-300 bg-white text-sm font-medium disabled:opacity-60"
            >
              <Pencil className="w-4 h-4" /> Mark contacted manually
            </button>
          </div>
        </>
      )}

      <div className="text-xs text-emerald-900/80">
        Link: <a href={link} target="_blank" rel="noopener noreferrer"
                  data-testid="confirmation-link-anchor"
                  className="underline">{link}</a>
      </div>

      {(draft.delivery_log || []).length > 0 && (
        <details className="text-xs text-emerald-900/80" data-testid="delivery-log">
          <summary className="cursor-pointer font-medium">
            Delivery history ({draft.delivery_log.length})
          </summary>
          <ul className="mt-2 space-y-1">
            {draft.delivery_log.map((e) => (
              <li key={e.id}>
                {e.at?.slice(0, 16).replace("T", " ")}: {e.action}
                {e.channel ? ` via ${e.channel}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
